import { useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { usePromptContext, type SortBy } from '../context/PromptContext';
import type { Prompt, Folder, FileSystem, AppSettings, ItemKind } from '../../types';
import { PencilIcon, PlusIcon, TrashIcon, StarIcon, DownloadIcon, FolderMoveIcon, CopyPlusIcon, KindGlyph } from '../../constants';
import {
  ITEM_KINDS,
  KIND_META,
  getKind,
  getSkillSlug,
  buildSkillMarkdown,
  buildWorkflowMarkdown,
  parseSkillMarkdown,
  searchableText,
  downloadText,
  downloadBlob,
  buildZipBlob,
  GEM_MAIN_FILE,
} from '../../utils/itemKinds';
import { announce } from '../../services/aria';
import {
  findFolder,
  getAllPrompts,
  updatePromptInFS,
  addPromptToFolderFS,
  addFolderToFS,
  deleteFolderFromFS,
  renameFolderInFS,
  setFolderColorInFS,
  findPromptAndParentFolder,
  restorePromptFromTrash,
  permanentlyDeletePrompt,
  removePromptFromFS,
  removePromptsFromFS,
  addPromptsToFolderFS,
  deletePromptFromFS,
  emptyTrash,
  moveFolderInFS,
} from '../../utils/fileSystemUtils.ts';

export type { SortBy };

type ContextMenuState = {
  x: number;
  y: number;
  options: { id: string; label: string; action: () => void; icon?: React.ReactNode; isDestructive?: boolean }[];
};

const usePromptActions = () => {
  const {
    fileSystem,
    setFileSystem,
    templates,
    setTemplates,
    selectedFolderId,
    setSelectedFolderId,
    selectedPromptId,
    setSelectedPromptId,
    activeTag,
    selectedSpecialView,
    setSelectedSpecialView,
    searchTerm,
    settings,
    setSettings,
    showToast,
    forceImmediateSync,
    modalState,
    setModalState,
    contextMenu,
    setContextMenu,
    closeSidebar,
    kindFilter,
    sortBy,
    setSortBy,
    pushHistory,
    undo,
    canUndo,
  } = usePromptContext();

  const setIsTemplateManagerOpen = useCallback((open: boolean) => {
    setSelectedSpecialView(open ? 'templates' : null);
  }, [setSelectedSpecialView]);

  const setIsPromptGeneratorOpen = useCallback((open: boolean) => {
    setSelectedSpecialView(open ? 'generator' : null);
  }, [setSelectedSpecialView]);

  const closeOnMobile = useCallback(() => {
    closeSidebar?.();
  }, [closeSidebar]);

  const handleUndo = useCallback(() => {
    const previous = undo();
    if (previous) {
      setFileSystem(previous.fileSystem);
      setTemplates(previous.templates);
      showToast('Aktion rückgängig gemacht');
      forceImmediateSync?.(previous.fileSystem, previous.templates);
    }
  }, [undo, setFileSystem, setTemplates, showToast, forceImmediateSync]);

  const handleUpdatePrompt = useCallback((updatedPrompt: Prompt) => {
    pushHistory();
    setFileSystem(current => updatePromptInFS(current, updatedPrompt));
  }, [setFileSystem, pushHistory]);

  const createNewPrompt = useCallback((folderId: string | null, fromTemplate?: Partial<Prompt>) => {
    pushHistory();
    const kind: ItemKind = fromTemplate?.kind ?? 'prompt';
    const now = new Date().toISOString();
    const newPrompt: Prompt = {
      ...(fromTemplate ? structuredClone(fromTemplate) : {}),
      id: uuidv4(),
      kind,
      name: fromTemplate?.name || KIND_META[kind].defaultName,
      content: fromTemplate?.content || '',
      tags: fromTemplate?.tags || [],
      isFavorite: fromTemplate?.isFavorite || false,
      createdAt: now,
      updatedAt: now,
      versions: fromTemplate?.versions || [],
      useCount: 0,
      lastUsed: null,
      deletedAt: null,
    };
    if (kind === 'workflow' && !(newPrompt.steps && newPrompt.steps.length)) {
      newPrompt.steps = [{ id: uuidv4(), title: '', content: '', refId: null }];
    }
    if ((kind === 'skill' || kind === 'gem') && !newPrompt.files) newPrompt.files = [];

    const targetFolderId = folderId ?? selectedFolderId ?? null;

    setFileSystem(currentFS => {
      const newFS: FileSystem = structuredClone(currentFS);
      let targetFolder = targetFolderId ? findFolder(newFS, targetFolderId) : newFS[0];
      if (!targetFolder) {
        if (newFS.length === 0) {
          const newRootFolder = { id: uuidv4(), name: 'Mein erster Ordner', prompts: [], subFolders: [] };
          newFS.push(newRootFolder);
          targetFolder = newRootFolder;
        } else {
          targetFolder = newFS[0];
        }
      }
      targetFolder.prompts.unshift(newPrompt);
      return newFS;
    });
    setSelectedPromptId(newPrompt.id);
    setSelectedSpecialView(null);
    setIsTemplateManagerOpen(false);
    setIsPromptGeneratorOpen(false);
    closeOnMobile();
  }, [pushHistory, selectedFolderId, setFileSystem, setSelectedPromptId, setSelectedSpecialView, setIsTemplateManagerOpen, setIsPromptGeneratorOpen, closeOnMobile]);

  /** Neuer Eintrag; ohne Angabe der Art gilt der aktive Art-Filter, sonst Prompt. */
  const handleNewPromptRequest = useCallback((folderId?: string | null, kind?: ItemKind) => {
    const targetFolderId = folderId ?? selectedFolderId ?? null;
    const resolvedKind: ItemKind = kind ?? (kindFilter !== 'all' ? kindFilter : 'prompt');
    createNewPrompt(targetFolderId, { kind: resolvedKind });
  }, [selectedFolderId, createNewPrompt, kindFilter]);

  const handleDeletePrompt = useCallback((id: string) => {
    pushHistory();
    setFileSystem(current => {
      const next = deletePromptFromFS(current, id);
      forceImmediateSync?.(next, templates);
      return next;
    });
    if (selectedPromptId === id) setSelectedPromptId(null);
    setModalState(null);
    showToast('In den Papierkorb verschoben', 'success', handleUndo);
  }, [selectedPromptId, setFileSystem, showToast, forceImmediateSync, templates, setSelectedPromptId, pushHistory, handleUndo]);

  const handleDeletePrompts = useCallback((ids: string[]) => {
    if (ids.length === 0) return;
    pushHistory();
    setFileSystem(current => {
      let next = current;
      ids.forEach(id => {
        next = deletePromptFromFS(next, id);
      });
      forceImmediateSync?.(next, templates);
      return next;
    });
    if (ids.includes(selectedPromptId || '')) setSelectedPromptId(null);
    showToast(`${ids.length} Einträge in den Papierkorb verschoben`, 'success');
  }, [selectedPromptId, setFileSystem, showToast, forceImmediateSync, templates, setSelectedPromptId, pushHistory]);

  const handleRestorePrompt = (id: string) => {
    pushHistory();
    setFileSystem(current => {
      const next = restorePromptFromTrash(current, id);
      forceImmediateSync?.(next, templates);
      return next;
    });
    if (selectedPromptId === id) setSelectedPromptId(null);
    showToast('Wiederhergestellt');
  };

  const handlePermanentDeletePrompt = (id: string) => {
    pushHistory();
    setFileSystem(current => {
      const next = permanentlyDeletePrompt(current, id);
      forceImmediateSync?.(next, templates);
      return next;
    });
    if (selectedPromptId === id) setSelectedPromptId(null);
    setModalState(null);
    showToast('Endgültig gelöscht', 'success');
  };

  const handleEmptyTrash = () => {
    pushHistory();
    setFileSystem(current => {
      const next = emptyTrash(current);
      forceImmediateSync?.(next, templates);
      return next;
    });
    setSelectedPromptId(null);
    showToast('Papierkorb geleert', 'success');
  };

  const handleSaveAsTemplate = (prompt: Prompt) => {
    pushHistory();
    const newTemplate = { ...prompt, id: uuidv4() };
    setTemplates(current => [...current, newTemplate]);
    showToast('Als Vorlage gespeichert');
  };

  const handleUpdateTemplate = (templateId: string, updates: Partial<Prompt>) => {
    pushHistory();
    setTemplates(current =>
      current.map(t => (t.id === templateId ? { ...t, ...updates, updatedAt: new Date().toISOString() } : t))
    );
    showToast('Vorlage aktualisiert');
  };

  const handleDeleteTemplate = (templateId: string) => {
    const templateToDelete = templates.find(t => t.id === templateId);
    if (!templateToDelete) return;
    pushHistory();
    setTemplates(current => current.filter(t => t.id !== templateId));
    showToast(`Vorlage „${templateToDelete.name}“ gelöscht`, 'success', () => {
      setTemplates(current => [...current, templateToDelete]);
      showToast('Vorlage wiederhergestellt');
    });
  };

  const handleUseTemplate = (template: Prompt) => {
    const targetFolderId = selectedFolderId ?? null;
    createNewPrompt(targetFolderId, template);
    setIsTemplateManagerOpen(false);
  };

  const allPrompts = useMemo(() => getAllPrompts(fileSystem), [fileSystem]);
  const activePrompts = useMemo(() => (allPrompts || []).filter(p => !p.deletedAt), [allPrompts]);
  const trashPrompts = useMemo(() => (allPrompts || []).filter(p => p.deletedAt), [allPrompts]);

  const handleDuplicatePrompt = useCallback((id: string, currentData?: Prompt) => {
    const original = currentData || allPrompts.find(p => p.id === id);
    if (!original) return;
    const info = findPromptAndParentFolder(fileSystem, id);
    const targetFolderId = info?.parent.id ?? selectedFolderId ?? null;
    pushHistory();
    const duplicated: Prompt = {
      ...structuredClone(original),
      id: uuidv4(),
      name: `${original.name} (Kopie)`,
      updatedAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      useCount: 0,
      lastUsed: null,
      deletedAt: null,
      versions: [],
    };
    setFileSystem(currentFS => {
      if (targetFolderId) {
        return addPromptToFolderFS(currentFS, targetFolderId, duplicated);
      }
      const newFS: FileSystem = structuredClone(currentFS);
      if (newFS.length > 0) {
        newFS[0].prompts.unshift(duplicated);
      }
      return newFS;
    });
    setSelectedPromptId(duplicated.id);
    showToast(`„${original.name}“ dupliziert`);
  }, [allPrompts, fileSystem, selectedFolderId, pushHistory, setFileSystem, setSelectedPromptId, showToast]);

  const handleTrackPromptUse = useCallback((promptId: string) => {
    setFileSystem(current => {
      const info = findPromptAndParentFolder(current, promptId);
      if (!info) return current;
      const updatedPrompt = {
        ...info.prompt,
        useCount: (info.prompt.useCount || 0) + 1,
        lastUsed: new Date().toISOString(),
      };
      return updatePromptInFS(current, updatedPrompt);
    });
  }, [setFileSystem]);

  const getFolderName = useCallback((promptId: string) => {
    const info = findPromptAndParentFolder(fileSystem, promptId);
    return info ? info.parent.name : '';
  }, [fileSystem]);

  const getFolderId = useCallback((promptId: string) => {
    const info = findPromptAndParentFolder(fileSystem, promptId);
    return info ? info.parent.id : null;
  }, [fileSystem]);

  const handleMovePrompt = (promptId: string, targetFolderId: string, targetIndex?: number) => {
    const targetFolder = findFolder(fileSystem, targetFolderId);
    if (!targetFolder) {
      showToast('Zielordner wurde nicht gefunden.', 'error');
      return;
    }
    const info = findPromptAndParentFolder(fileSystem, promptId);
    if (!info) return;
    const { prompt, parent } = info;
    let insertIndex = targetIndex;
    if (parent.id === targetFolderId) {
      const oldIndex = parent.prompts.findIndex(p => p.id === promptId);
      if (insertIndex === undefined || oldIndex === insertIndex) return;
      if (insertIndex > oldIndex) insertIndex--;
    }
    pushHistory();
    setFileSystem(currentFS => {
      if (!findFolder(currentFS, targetFolderId)) return currentFS;
      const fsWithoutPrompt = removePromptFromFS(currentFS, promptId);
      return addPromptToFolderFS(fsWithoutPrompt, targetFolderId, prompt, insertIndex);
    });
    showToast('Verschoben');
    announce('Eintrag verschoben');
  };

  const handleMovePrompts = (promptIds: string[], targetFolderId: string, targetIndex?: number) => {
    if (promptIds.length === 0) return;
    const targetFolder = findFolder(fileSystem, targetFolderId);
    if (!targetFolder) {
      showToast('Zielordner wurde nicht gefunden.', 'error');
      return;
    }
    // Gather prompts in current fs order
    const promptsToMove = promptIds
      .map(id => findPromptAndParentFolder(fileSystem, id)?.prompt)
      .filter(Boolean) as Prompt[];
    if (promptsToMove.length === 0) return;

    pushHistory();
    setFileSystem(currentFS => {
      if (!findFolder(currentFS, targetFolderId)) return currentFS;
      const fsWithout = removePromptsFromFS(currentFS, promptIds);
      return addPromptsToFolderFS(fsWithout, targetFolderId, promptsToMove, targetIndex);
    });
    showToast(`${promptIds.length} Einträge verschoben`);
    announce(`${promptIds.length} Einträge verschoben`);
  };

  const handleCreateFolder = (name: string, parentId: string | null) => {
    pushHistory();
    const newFolder: Folder = { id: uuidv4(), name, prompts: [], subFolders: [] };
    setFileSystem(current => addFolderToFS(current, parentId, newFolder));
    setModalState(null);
  };

  const handleSetFolderColor = useCallback((folderId: string, color: string | undefined) => {
    setFileSystem(prev => setFolderColorInFS(prev, folderId, color));
    setModalState(null);
  }, [setFileSystem]);

  const handleRenameFolder = (folderId: string, newName: string) => {
    pushHistory();
    setFileSystem(current => renameFolderInFS(current, folderId, newName));
    setModalState(null);
  };

  const handleMoveFolder = (folderId: string, targetParentId: string | null, index?: number) => {
    pushHistory();
    setFileSystem(current => moveFolderInFS(current, folderId, targetParentId, index));
    showToast('Ordner verschoben');
    announce('Ordner verschoben');
  };

  const handleDeleteFolder = (folderId: string) => {
    if (selectedFolderId === folderId) setSelectedFolderId(null);
    pushHistory();
    setFileSystem(current => deleteFolderFromFS(current, folderId));
    setModalState(null);
  };

  const handleExportPrompt = useCallback((promptId: string, currentData?: Prompt) => {
    const promptToExport = currentData || allPrompts.find(p => p.id === promptId);
    if (!promptToExport) {
      showToast('Eintrag nicht gefunden.', 'error');
      return;
    }
    const kind = getKind(promptToExport);
    if (kind === 'skill') {
      const files = (promptToExport.files || []).filter(f => f.path.trim());
      if (files.length > 0) {
        const zipBlob = buildZipBlob([
          { path: 'SKILL.md', content: buildSkillMarkdown(promptToExport) },
          ...files.map(f => ({ path: f.path, content: f.content }))
        ]);
        downloadBlob(`${getSkillSlug(promptToExport)}.zip`, zipBlob);
      } else {
        downloadText('SKILL.md', buildSkillMarkdown(promptToExport));
      }
      showToast(`Skill „${getSkillSlug(promptToExport)}“ heruntergeladen`);
      return;
    }
    if (kind === 'workflow') {
      const md = buildWorkflowMarkdown(promptToExport, id => allPrompts.find(p => p.id === id));
      const filename = (promptToExport.name || 'workflow').replace(/[^a-z0-9äöüß]+/gi, '-').toLowerCase();
      // Gesyncte Claude-Agenten bringen ihre Originaldatei mit; die gehört in den Download
      const files = (promptToExport.files || []).filter(f => f.path.trim());
      if (files.length > 0) {
        downloadBlob(`workflow-${filename}.zip`, buildZipBlob([{ path: `workflow-${filename}.md`, content: md }, ...files]));
      } else {
        downloadText(`workflow-${filename}.md`, md);
      }
      showToast(`Workflow „${promptToExport.name}“ heruntergeladen`);
      return;
    }
    if (kind === 'gem') {
      const files = (promptToExport.files || []).filter(f => f.path.trim());
      const filename = getSkillSlug(promptToExport);
      if (files.length > 0) {
        downloadBlob(`${filename}.zip`, buildZipBlob([{ path: GEM_MAIN_FILE, content: promptToExport.content || '' }, ...files]));
      } else {
        downloadText(`${filename}-${GEM_MAIN_FILE}`, promptToExport.content || '');
      }
      showToast(`Gem „${promptToExport.name}“ heruntergeladen`);
      return;
    }
    const blob = new Blob([JSON.stringify(promptToExport, null, 2)], {
      type: 'application/json',
    });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    const filename = (promptToExport.name || 'untitled')
      .replace(/[^a-z0-9]/gi, '_')
      .toLowerCase();
    link.download = `prompt-${filename}.json`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    showToast(`„${promptToExport.name || 'Ohne Titel'}“ exportiert`);
  }, [allPrompts, showToast]);

  type FolderContextData = { name: string };
  type PromptContextData = Pick<Prompt, 'name' | 'isFavorite'> & { kind?: ItemKind };
  const handleOpenContextMenu = (
    e: React.MouseEvent,
    type: 'folder' | 'prompt',
    id: string,
    data: FolderContextData | PromptContextData,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    let options: ContextMenuState['options'] = [];
    if (type === 'folder') {
      const folderData = data as FolderContextData;
      options = [
        ...ITEM_KINDS.map(kind => ({
          id: `new-${kind}`,
          label: KIND_META[kind].newLabel,
          icon: <span className={KIND_META[kind].text}><KindGlyph kind={kind} /></span>,
          action: () => createNewPrompt(id, { kind }),
        })),
        { id: 'new-folder', label: 'Neuer Ordner', icon: <PlusIcon />, action: () => setModalState({ type: 'create-folder', parentId: id }) },
        { id: 'rename-folder', label: 'Umbenennen', icon: <PencilIcon />, action: () => setModalState({ type: 'rename-folder', folderId: id, currentName: folderData.name }) },
        { id: 'color-folder', label: 'Farbe ändern', icon: <span className="text-base leading-none">🎨</span>, action: () => {
            const folder = findFolder(fileSystem, id);
            setModalState({ type: 'folder-color', folderId: id, currentColor: folder?.color });
          } },
        { id: 'delete-folder', label: 'Löschen', icon: <TrashIcon />, isDestructive: true, action: () => setModalState({ type: 'delete-folder', folderId: id, folderName: folderData.name }) },
      ];
    } else if (type === 'prompt') {
      const promptData = data as PromptContextData;
      options = [
        { id: 'toggle-favorite', label: promptData.isFavorite ? 'Von Favoriten entfernen' : 'Zu Favoriten hinzufügen', icon: <StarIcon filled={!promptData.isFavorite} />, action: () => {
            const prompt = allPrompts.find(p => p.id === id);
            if (prompt) handleUpdatePrompt({ ...prompt, isFavorite: !prompt.isFavorite });
          } },
        { id: 'duplicate-prompt', label: 'Duplizieren', icon: <CopyPlusIcon />, action: () => handleDuplicatePrompt(id) },
        { id: 'move-prompt', label: 'Verschieben', icon: <FolderMoveIcon />, action: () => setModalState({ type: 'move-prompt', promptId: id }) },
        { id: 'export-prompt', label: promptData.kind === 'skill' ? 'SKILL.md herunterladen' : promptData.kind === 'workflow' ? 'Als Markdown herunterladen' : promptData.kind === 'gem' ? 'Systemprompt herunterladen' : 'Als JSON exportieren', icon: <DownloadIcon />, action: () => handleExportPrompt(id) },
        { id: 'delete-prompt', label: 'Löschen', icon: <TrashIcon />, isDestructive: true, action: () => setModalState({ type: 'delete-prompt', promptId: id, promptName: promptData.name }) },
      ];
    }
    setContextMenu({ x: e.clientX, y: e.clientY, options });
  };

  const handleExport = () => {
    setModalState({ type: 'export-format' });
  };

  const handleExportJson = () => {
    const userData = { fileSystem, templates };
    const blob = new Blob([JSON.stringify(userData, null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `prompt-manager-backup-${new Date().toISOString().slice(0, 10)}.json`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
  };

  const handleExportMarkdown = () => {
    const prompts = getAllPrompts(fileSystem).filter(p => !p.deletedAt);
    const lines: string[] = [
      '# Prompt Manager Pro — Export',
      `> Exportiert am ${new Date().toLocaleString('de-DE')}`,
      `> ${prompts.length} Einträge`,
      '',
    ];
    prompts.forEach(p => {
      const kind = getKind(p);
      if (kind === 'skill') {
        lines.push(`## Skill: ${p.name}`, '', '````markdown', buildSkillMarkdown(p).trimEnd(), '````', '');
        (p.files || []).forEach(f => lines.push(`**${f.path}**`, '', '````', f.content, '````', ''));
        lines.push('---', '');
        return;
      }
      if (kind === 'gem') {
        lines.push(`## Gem: ${p.name}`, '');
        if (p.description) lines.push(`> ${p.description}`, '');
        lines.push('````markdown', (p.content || '').trimEnd(), '````', '');
        (p.files || []).forEach(f => lines.push(`**${f.path}**`, '', '````', f.content, '````', ''));
        lines.push('---', '');
        return;
      }
      if (kind === 'workflow') {
        lines.push(`## Workflow: ${p.name}`, '', buildWorkflowMarkdown(p, id => allPrompts.find(x => x.id === id), { embedReferences: false }).replace(/^# .*\n/, '').replace(/^## /gm, '### '), '---', '');
        return;
      }
      lines.push(`## ${p.name}`);
      if (p.description) lines.push(`> ${p.description}`, '');
      lines.push(
        `**Tags:** ${(p.tags || []).length ? (p.tags || []).map(t => `\`${t}\``).join(', ') : '—'}  `,
        `**Erstellt:** ${p.createdAt ? new Date(p.createdAt).toLocaleDateString('de-DE') : '—'}  `,
        `**Verwendet:** ${p.useCount}×`,
        '',
        '```',
        p.content,
        '```',
        '',
        '---',
        '',
      );
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/markdown;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `bibliothek-${new Date().toISOString().slice(0, 10)}.md`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    setModalState(null);
  };

  const handleExportCsv = () => {
    const prompts = getAllPrompts(fileSystem).filter(p => !p.deletedAt);
    const escape = (s: string) => '"' + (s ?? '').replace(/"/g, '""').replace(/\n/g, ' ') + '"';
    const header = ['Art', 'Name', 'Beschreibung', 'Inhalt', 'Tags', 'Favorit', 'Verwendet', 'Zuletzt verwendet', 'Erstellt', 'Aktualisiert'];
    const rows = prompts.map(p => [
      KIND_META[getKind(p)].label,
      escape(p.name),
      escape(p.description ?? ''),
      escape(getKind(p) === 'workflow' ? (p.steps || []).map((st, i) => `${i + 1}. ${st.title}: ${st.content}`).join(' | ') : p.content),
      escape((p.tags || []).join('; ')),
      p.isFavorite ? 'Ja' : 'Nein',
      String(p.useCount),
      p.lastUsed ? new Date(p.lastUsed).toLocaleDateString('de-DE') : '',
      p.createdAt ? new Date(p.createdAt).toLocaleDateString('de-DE') : '',
      new Date(p.updatedAt).toLocaleDateString('de-DE'),
    ]);
    const csv = [header.join(';'), ...rows.map(r => r.join(';'))].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' }); // BOM for Excel
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `bibliothek-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    setModalState(null);
  };

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json,.json';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const importedData = JSON.parse(event.target?.result as string);
          const isValidFS = Array.isArray(importedData?.fileSystem) &&
            importedData.fileSystem.every((f: any) => typeof f === 'object' && f !== null && typeof f.id === 'string' && typeof f.name === 'string');
          const isValidTemplates = Array.isArray(importedData?.templates);

          if (isValidFS && isValidTemplates) {
            setModalState({ type: 'confirm-import', data: importedData.fileSystem, templates: importedData.templates });
          } else {
            showToast('Fehler: Ungültiges Dateiformat.', 'error');
          }
        } catch {
          showToast('Fehler: Datei konnte nicht gelesen werden.', 'error');
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  /** Liest eine oder mehrere SKILL.md-Dateien und legt je Datei einen Skill an. */
  const handleImportSkill = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.md,text/markdown,text/plain';
    input.multiple = true;
    input.onchange = async (e) => {
      const files = Array.from((e.target as HTMLInputElement).files || []);
      if (files.length === 0) return;
      const parsed = await Promise.all(files.map(async file => ({ file, data: parseSkillMarkdown(await file.text()) })));
      const valid = parsed.filter(p => p.data.body || p.data.description);
      if (valid.length === 0) {
        showToast('Keine lesbare SKILL.md gefunden. Erwartet wird Frontmatter mit name und description.', 'error');
        return;
      }
      pushHistory();
      const now = new Date().toISOString();
      const newItems: Prompt[] = valid.map(({ file, data }) => {
        const slug = data.slug || file.name.replace(/\.md$/i, '').toLowerCase();
        return {
          id: uuidv4(),
          kind: 'skill',
          name: slug,
          slug,
          description: data.description,
          content: data.body,
          files: [],
          tags: [],
          isFavorite: false,
          createdAt: now,
          updatedAt: now,
          versions: [],
          useCount: 0,
          lastUsed: null,
          deletedAt: null,
        };
      });
      setFileSystem(currentFS => {
        const newFS: FileSystem = structuredClone(currentFS);
        let target = selectedFolderId ? findFolder(newFS, selectedFolderId) : newFS.find(f => f.id !== 'trash');
        if (!target) {
          target = { id: uuidv4(), name: 'Skills', prompts: [], subFolders: [] };
          newFS.push(target);
        }
        target.prompts.unshift(...newItems);
        return newFS;
      });
      setSelectedSpecialView(selectedFolderId ? null : 'all');
      setSelectedPromptId(newItems[0].id);
      showToast(newItems.length === 1 ? `Skill „${newItems[0].slug}“ importiert` : `${newItems.length} Skills importiert`);
    };
    input.click();
  };

  const handleCloseGeneratorAndCreatePrompt = (promptContent?: string) => {
    setIsPromptGeneratorOpen(false);
    setSelectedSpecialView('all');
    if (promptContent) {
      createNewPrompt(null, {
        id: '',
        name: 'Generierter Prompt',
        content: promptContent,
        tags: ['generiert'],
        isFavorite: false,
        updatedAt: '',
        versions: [],
        useCount: 0,
        lastUsed: null,
      });
      showToast('Neuer Prompt wurde erstellt!');
    }
  };

  const allTags = useMemo(() => [...new Set((activePrompts || []).flatMap(p => p.tags || []))], [activePrompts]);

  const applySorting = useCallback((items: Prompt[]): Prompt[] => {
    switch (sortBy) {
      case 'name-asc':
        return [...items].sort((a, b) => a.name.localeCompare(b.name, 'de'));
      case 'name-desc':
        return [...items].sort((a, b) => b.name.localeCompare(a.name, 'de'));
      case 'date-old':
        return [...items].sort((a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime());
      case 'usage':
        return [...items].sort((a, b) => (b.useCount || 0) - (a.useCount || 0));
      case 'favorites':
        return [...items].sort((a, b) => (b.isFavorite ? 1 : 0) - (a.isFavorite ? 1 : 0) || new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      case 'date-new':
      default:
        return [...items].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    }
  }, [sortBy]);

  /** Einträge der aktuellen Ansicht vor dem Art-Filter; Basis für Zähler und Liste */
  const viewPrompts = useMemo(() => {
    let prompts: Prompt[] = [];
    if (selectedSpecialView === 'trash') {
      prompts = [...trashPrompts].sort((a, b) => new Date(b.deletedAt!).getTime() - new Date(a.deletedAt!).getTime());
    } else if (selectedSpecialView === 'recent') {
      const touched = (p: Prompt) => Math.max(new Date(p.lastUsed || 0).getTime(), new Date(p.updatedAt || 0).getTime());
      prompts = [...activePrompts]
        .sort((a, b) => touched(b) - touched(a))
        .slice(0, 20);
    } else if (activeTag) {
      prompts = activePrompts.filter(p => (p.tags || []).includes(activeTag));
    } else if (selectedSpecialView === 'favorites') {
      prompts = activePrompts.filter(p => p.isFavorite);
    } else if (selectedFolderId) {
      const folder = findFolder(fileSystem, selectedFolderId);
      prompts = folder ? [...(folder.prompts || [])].filter(p => !p.deletedAt) : [];
    } else {
      prompts = [...activePrompts];
    }
    const term = searchTerm.trim().toLowerCase();
    if (term) {
      prompts = (prompts || []).filter(p => searchableText(p).includes(term));
    }
    return prompts;
  }, [fileSystem, activeTag, selectedFolderId, selectedSpecialView, searchTerm, activePrompts, trashPrompts]);

  const kindCounts = useMemo(() => {
    const counts: Record<ItemKind | 'all', number> = { all: viewPrompts.length, prompt: 0, skill: 0, workflow: 0, gem: 0 };
    viewPrompts.forEach(p => { counts[getKind(p)]++; });
    return counts;
  }, [viewPrompts]);

  const libraryCounts = useMemo(() => {
    const counts: Record<ItemKind | 'all', number> = { all: activePrompts.length, prompt: 0, skill: 0, workflow: 0, gem: 0 };
    activePrompts.forEach(p => { counts[getKind(p)]++; });
    return counts;
  }, [activePrompts]);

  const displayedPrompts = useMemo(() => {
    let prompts = kindFilter === 'all' ? viewPrompts : viewPrompts.filter(p => getKind(p) === kindFilter);
    // Papierkorb und „Zuletzt verwendet“ behalten ihre feste Reihenfolge
    if (selectedSpecialView !== 'trash' && selectedSpecialView !== 'recent') {
      prompts = applySorting(prompts);
    }
    return prompts;
  }, [viewPrompts, kindFilter, selectedSpecialView, applySorting]);

  const selectedPrompt = useMemo(() => allPrompts.find(p => p.id === selectedPromptId) || null, [allPrompts, selectedPromptId]);

  const getListTitle = () => {
    if (activeTag) return `Tag: #${activeTag}`;
    if (selectedSpecialView === 'recent') return 'Zuletzt verwendet';
    if (selectedSpecialView === 'favorites') return 'Favoriten';
    if (selectedSpecialView === 'templates') return 'Vorlagen';
    if (selectedSpecialView === 'generator') return 'Prompt-Generator';
    if (selectedSpecialView === 'trash') return 'Papierkorb';
    if (selectedFolderId) return findFolder(fileSystem, selectedFolderId)?.name || 'Ordner';
    return kindFilter === 'all' ? 'Bibliothek' : `Alle ${KIND_META[kindFilter].plural}`;
  };

  const handleUpdateSettings = useCallback((updates: Partial<AppSettings>) => {
    if (setSettings && settings) {
      setSettings({ ...settings, ...updates });
    }
  }, [setSettings, settings]);

  return useMemo(() => ({

    modalState,
    setModalState,
    contextMenu,
    setContextMenu,
    handleUpdatePrompt,
    createNewPrompt,
    handleNewPromptRequest,
    handleDeletePrompt,
    handleDeletePrompts,
    handleDuplicatePrompt,
    handleRestorePrompt,
    handlePermanentDeletePrompt,
    handleEmptyTrash,
    handleUndo,
    canUndo,
    handleSaveAsTemplate,
    handleUpdateTemplate,
    handleDeleteTemplate,
    handleUseTemplate,
    handleTrackPromptUse,
    handleMovePrompt,
    handleMovePrompts,
    handleCreateFolder,
    handleRenameFolder,
    handleDeleteFolder,
    handleMoveFolder,
    handleSetFolderColor,
    handleExportPrompt,
    getFolderName,
    getFolderId,
    handleOpenContextMenu,
    handleExport,
    handleExportJson,
    handleExportMarkdown,
    handleExportCsv,
    handleImport,
    handleImportSkill,
    handleCloseGeneratorAndCreatePrompt,
    allTags,
    displayedPrompts,
    kindCounts,
    libraryCounts,
    selectedPrompt,
    getListTitle,
    allPrompts,
    templates,
    trashPrompts,
    settings,
    handleUpdateSettings,
    sortBy,
    setSortBy,
  }), [

    modalState,
    contextMenu,
    handleUpdatePrompt,
    createNewPrompt,
    handleNewPromptRequest,
    handleDeletePrompt,
    handleDeletePrompts,
    handleDuplicatePrompt,
    handleRestorePrompt,
    handlePermanentDeletePrompt,
    handleEmptyTrash,
    handleUndo,
    canUndo,
    handleSaveAsTemplate,
    handleUpdateTemplate,
    handleDeleteTemplate,
    handleUseTemplate,
    handleTrackPromptUse,
    handleMovePrompt,
    handleMovePrompts,
    handleCreateFolder,
    handleRenameFolder,
    handleDeleteFolder,
    handleMoveFolder,
    handleSetFolderColor,
    handleExportPrompt,
    getFolderName,
    getFolderId,
    handleOpenContextMenu,
    allTags,
    displayedPrompts,
    kindCounts,
    libraryCounts,
    selectedPrompt,
    settings,
    handleUpdateSettings,
    templates,
    sortBy,
  ]);
};


export default usePromptActions;
